export function compositeTransparentPixelsOverWhite(input: ImageData): ImageData {
  const out = new ImageData(input.width, input.height);
  const src = input.data;
  const dst = out.data;

  for (let i = 0; i < src.length; i += 4) {
    const alpha = (src[i + 3] ?? 255) / 255;
    const invAlpha = 1 - alpha;

    dst[i] = Math.round((src[i] ?? 0) * alpha + 255 * invAlpha);
    dst[i + 1] = Math.round((src[i + 1] ?? 0) * alpha + 255 * invAlpha);
    dst[i + 2] = Math.round((src[i + 2] ?? 0) * alpha + 255 * invAlpha);
    dst[i + 3] = 255;
  }

  return out;
}

export interface ReferenceOverlayCompositeOptions {
  image: ImageData | null;
  enabled: boolean;
  opacity: number;
}

export function compositeReferenceOverlay(
  base: ImageData,
  overlay: ReferenceOverlayCompositeOptions,
): ImageData {
  if (!overlay.enabled || !overlay.image || overlay.opacity <= 0) return base;

  const opacity = Math.min(1, Math.max(0, overlay.opacity));
  const out = new ImageData(new Uint8ClampedArray(base.data), base.width, base.height);
  const src = overlay.image.data;
  const dst = out.data;
  const width = Math.min(base.width, overlay.image.width);
  const height = Math.min(base.height, overlay.image.height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const si = (y * overlay.image.width + x) * 4;
      const di = (y * base.width + x) * 4;
      const alpha = ((src[si + 3] ?? 0) / 255) * opacity;
      if (alpha <= 0) continue;
      const invAlpha = 1 - alpha;
      dst[di] = Math.round((src[si] ?? 0) * alpha + (dst[di] ?? 0) * invAlpha);
      dst[di + 1] = Math.round((src[si + 1] ?? 0) * alpha + (dst[di + 1] ?? 0) * invAlpha);
      dst[di + 2] = Math.round((src[si + 2] ?? 0) * alpha + (dst[di + 2] ?? 0) * invAlpha);
      const baseAlpha = (dst[di + 3] ?? 255) / 255;
      dst[di + 3] = Math.round((alpha + baseAlpha * invAlpha) * 255);
    }
  }

  return out;
}

export function hasReferenceOverlayDimensionMismatch(
  base: ImageData,
  overlay: ImageData | null,
): boolean {
  return overlay !== null && (overlay.width !== base.width || overlay.height !== base.height);
}

/**
 * Nearest-neighbour upscale of an ImageData by an integer factor.
 * Preserves all four RGBA channels so it handles both greyscale
 * source images and RGBA-encoded labelmaps (red channel = label).
 * Returns the input unchanged when scale <= 1.
 */
export function nearestUpscale(image: ImageData, scale: number): ImageData {
  const s = Math.max(1, Math.floor(scale));
  if (s <= 1) return image;

  const { width: w, height: h, data: src } = image;
  const outW = w * s;
  const outH = h * s;
  const out = new ImageData(outW, outH);
  const dst = out.data;

  for (let y = 0; y < outH; y++) {
    const srcY = Math.floor(y / s);
    for (let x = 0; x < outW; x++) {
      const srcX = Math.floor(x / s);
      const si = (srcY * w + srcX) * 4;
      const di = (y * outW + x) * 4;
      dst[di] = src[si]!;
      dst[di + 1] = src[si + 1]!;
      dst[di + 2] = src[si + 2]!;
      dst[di + 3] = src[si + 3]!;
    }
  }

  return out;
}
